import { formatTimeAgo } from '@/utils/dateUtils';

import type { NotificationResponseDTO, NotificationModel } from '@/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = (key: string, options?: any) => string;

// ─────────────────────────────────────────────────────────────────
// Notification DTO → UI Model mapper
// Matches Xamarin NotificationModel computed fields + SetNotificationDetails()
// ─────────────────────────────────────────────────────────────────

/**
 * Determine whether a notification is "new" (unread).
 *
 * Matches Xamarin SetNotificationDetails():
 *   if (string.IsNullOrWhiteSpace(lastNotificationTime))
 *       x.IsNew = true;
 *   else if (DateTime.Parse(lastNotificationTime) < DateTime.Parse(x.UpdatedAt))
 *       x.IsNew = true;
 *   else
 *       x.IsNew = false;
 */
function computeIsNew(updatedAt: string, lastNotificationTime: string | null): boolean {
  if (!lastNotificationTime) return true;
  const lastTime = new Date(lastNotificationTime).getTime();
  const notifTime = new Date(updatedAt).getTime();
  return notifTime > lastTime;
}

/**
 * Map a NotificationResponseDTO (API snake_case) to NotificationModel (UI camelCase).
 * Computes derived fields: updatedAgo, isNew, isShowTaskNotification,
 * isShowTrailerNotification, taskDescription, trailerTypeStatusName.
 *
 * @param dto — raw API response
 * @param lastNotificationTime — ISO string from localStorage (null = all are new)
 */
export function mapNotificationDTO(
  dto: NotificationResponseDTO,
  lastNotificationTime: string | null,
  t: TranslateFn
): NotificationModel {
  const notificationType = dto.notification_type;
  const isTask = notificationType === 'Task';
  const isTrailer = notificationType === 'Trailer';

  const isNew = computeIsNew(dto.updatedAt, lastNotificationTime);

  return {
    id: dto._id,
    updatedAt: dto.updatedAt,
    actionCode: dto.action_code,
    orgId: dto.org_id,
    trailerId: dto.trailer_id,
    trailerName: dto.trailer_name,
    trailerComments: dto.trailer_comments,
    carrierName: dto.carrier_name,
    carrierScac: dto.carrier_scac,
    trailerStatus: dto.trailer_status,
    currentLocation: dto.curr_locn_name,
    createdAt: dto.createdAt,
    taskId: dto.task_id != null ? String(dto.task_id) : '',
    taskType: dto.task_type,
    status: dto.status,
    assignedUserId: dto.assigned_user_id,
    createdSource: dto.created_source,
    createdDateTime: dto.created_datetime,
    lastUpdatedDateTime: dto.last_updated_datetime,
    lastUpdatedSource: dto.last_updated_source,
    priority: dto.priority ?? 0,
    yardId: dto.yard_id,
    taskStartDateTime: dto.task_start_datetime ?? '',
    taskEndDateTime: dto.task_end_datetime ?? '',
    taskReleaseDatetime: dto.task_release_datetime,
    taskComments: dto.task_comments,
    carrierCompanyName: dto.carrier_company_name,
    auditLocationType: dto.audit_locn_type,
    auditLocationZone: dto.audit_locn_zone,
    auditLocationName: dto.audit_locn_name,
    moveSourceType: dto.move_src_type,
    moveSourceZone: dto.move_src_zone,
    moveSourceName: dto.move_src_name,
    moveDestinationType: dto.move_dst_type,
    moveDestinationZone: dto.move_dst_zone,
    moveDestinationName: dto.move_dst_name,
    notificationType,
    updatedAgo: formatTimeAgo(dto.updatedAt, t),
    isNew,
    taskDescription: '',
    trailerTypeStatusName: isTrailer ? dto.trailer_status : '',
    isShowTaskNotification: isTask,
    isShowTrailerNotification: isTrailer,
  };
}
